import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BowlFood, Storefront, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, offerList, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALT = {
  fruit: "Tubs of freshly cut fruit salad — watermelon, pineapple, melon and grapes — on a fridge shelf",
  salads: "Fresh garden and chicken caesar salad bowls with clear lids on a chilled shelf",
  sandwiches: "Packed sandwiches and rolled wraps on a chilled shelf",
  drinks: "Greek yoghurt pots, overnight oats, milk and orange juice on a fridge shelf",
  hot: "Fresh soup tubs and chilled ready meals on a refrigerated shelf",
  icecream: "Tubs of ice cream and fruit lollies in a workplace freezer drawer",
  fridge: "An open workplace fridge fully stocked with cut fruit, salads, sandwiches and yoghurts",
};

export default function FridgePublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/fridge/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ sections: [] }));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Workplace fridge", "/workplace-fridge"]]),
      localBusiness({
        name: "Unity & Utility workplace fridge",
        description: "Fresh cut fruit salad, salad bowls, sandwiches, yoghurts, soup and ready meals picked up from local shops on a standing weekly schedule and left in the workplace fridge free for employees.",
        path: "/workplace-fridge",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        type: "FoodEstablishment",
        area: "Worldwide",
      }),
      offerList("In the workplace fridge", "/workplace-fridge",
        (board?.sections || []).flatMap((s) => s.items.map((i) => ({
          name: i.name,
          description: `${s.label} · keeps ${i.keeps_days} day${i.keeps_days === 1 ? "" : "s"} in the fridge`,
          unit: `per ${i.unit}`,
        })))),
    ],
  };

  useSeo({
    title: "The workplace fridge — fresh cut fruit salad, salads and sandwiches free at work | Unity & Utility",
    description: "Fresh cut fruit salad, salad bowls, sandwiches and wraps, yoghurts, milk, juice, soup and ready meals picked up from Jewel-Osco, Tony's Fresh Market, Mariano's and other local shops on a weekly schedule and left in the workplace fridge free for everyone. Use-by dates, an eat-me-today shelf and a waste log. Pictures redrawn by AI every 24 hours.",
    path: "/workplace-fridge",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="fridge-public-page">
        <Crumbs items={[["Home", "/"], ["Workplace fridge", "/workplace-fridge"]]} />
        <p className="overline text-[#0B5C2E]">Fresh food at work</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <BowlFood size={34} weight="duotone" className="mt-1 shrink-0 text-[#0B5C2E]" />
          What's in the fridge today
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="fridge-public-intro">
          Fresh cut fruit salad, melon and grape bowls, salads, sandwiches and wraps, yoghurt
          pots, milk, juice, soup and chilled ready meals — picked up from the local shops on a
          standing weekly schedule and left in the workplace fridge, free for everyone on shift.
          Every tub goes in with its use-by date, so there is an eat-me-today shelf and a waste
          log rather than a drawer of forgotten melon. Pictures on this board are redrawn by AI
          every 24 hours, so you are always looking at today.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/workplace-fridge`}
            text="Fresh cut fruit salad, salads and sandwiches in the workplace fridge — free at work."
            testid="fridge-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt={ALT.fridge} loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="fridge-public-image" />
        )}

        <section className="mt-6 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid="fridge-public-card">
          <p className="overline text-[#0B5C2E]">
            Today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[["Fridges running", board?.fridges, "fridge-stat-fridges"],
              ["Lots in stock", board?.stocked, "fridge-stat-stocked"],
              ["Shops we buy from", (board?.shops || []).length, "fridge-stat-shops"]].map(([l, v, t]) => (
              <div key={t} className="border border-border bg-white p-3" data-testid={t}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{v ?? 0}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">
            Paid for by the employer and free to take. Nothing on this board names a workplace,
            a fridge or a person.
          </p>
        </section>

        {(board?.sections || []).map((s, si) => (
          <section key={s.key} className="mt-10" data-testid={`fridge-public-section-${s.key}`}>
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              {s.label}
            </h2>
            {board?.section_images?.[s.key] && (
              <img src={`${BACKEND}${board.section_images[s.key]}`} alt={ALT[s.key] || s.label}
                loading="lazy" className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
                data-testid={`fridge-public-image-${s.key}`} />
            )}
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {s.items.map((i, ii) => (
                <div key={i.name} className="border border-border bg-white p-4"
                  data-testid={`fridge-public-item-${si}-${ii}`}>
                  <p className="font-semibold text-[#0F172A]">{i.name}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    By the {i.unit} · keeps {i.keeps_days} day{i.keeps_days === 1 ? "" : "s"} in the fridge
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <ShareBar
                url={`${window.location.origin}/workplace-fridge`}
                text={`${s.label} in the workplace fridge today — fresh in from the local shops, free at work.`}
                testid={`fridge-public-share-${s.key}`} compact instagram />
            </div>
          </section>
        ))}

        {board?.section_images?.fridge && (
          <section className="mt-10" data-testid="fridge-public-section-fridge-photo">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              The fridge, stocked up
            </h2>
            <img src={`${BACKEND}${board.section_images.fridge}`} alt={ALT.fridge} loading="lazy"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="fridge-public-image-fridge" />
          </section>
        )}

        <section className="mt-10" data-testid="fridge-public-shops">
          <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            <Storefront size={18} weight="duotone" className="text-[#002FA7]" />
            Shops the run goes to
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {(board?.shops || []).map((shop) => (
              <span key={shop} className="border border-border bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                {shop}
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">
            An employer can add any local shop or convenience store on top of these, anywhere in
            the world, and order online for delivery straight to the fridge.
          </p>
        </section>

        <section className="mt-10 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-4" data-testid="fridge-public-how">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <Clock size={18} weight="duotone" className="text-[#002FA7]" />
            How it works
          </h2>
          <ul className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-700">
            <li>An employer registers the fridge and picks the shops it buys from.</li>
            <li>Days are set once — a standing weekly run, plus one-off top-ups when needed.</li>
            <li>A named runner collects it, or the store delivers straight to the fridge.</li>
            <li>Everything is logged in with its use-by date, so nothing hides at the back.</li>
            <li>Staff take what they want; what goes past its date goes on the waste log.</li>
          </ul>
          <Link to="/fridge" data-testid="fridge-public-open-desk"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#002FA7] underline">
            Open the fridge desk
          </Link>
        </section>

        <div className="mt-10">
          <NotifyMe source="workplace-fridge" testid="fridge-public-notify"
            blurb="Shops and runners are joining all the time. Leave your email and the town your workplace is in, and we will tell you when a fresh food run covers your area." />
        </div>
      </div>
    </PublicShell>
  );
}
